import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSupabaseQuery } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-900 dark:text-white">{value}</span>
    </div>
  )
}

function Separator() {
  return <hr className="border-gray-200 dark:border-gray-700" />
}

export default function ClicksTab({ selectedAccount }) {
  const [searchParams] = useSearchParams()
  const [selectedUrlId, setSelectedUrlId] = useState(null)
  const [dateRange, setDateRange] = useState('all')
  const [deviceFilter, setDeviceFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedClick, setSelectedClick] = useState(null)

  // Sync the `urlId` query param into the filter on mount (drill-down from
  // the dashboard Top-10 table). Read once — after that, the user owns
  // filter state.
  useEffect(() => {
    const id = searchParams.get('urlId')
    if (id) setSelectedUrlId(Number(id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: urls } = useSupabaseQuery('short_urls',
    selectedAccount ? {
      eq: { account_id: selectedAccount.id, is_deleted: false },
      select: 'id,slug,shortened_url'
    } : null
  )

  const { data: allClicks, loading, error } = useSupabaseQuery('short_url_click_events', {
    order: { column: 'timestamp', ascending: false },
    limit: 500
  })

  // Filter clicks to only those belonging to this account's URLs
  const urlIds = useMemo(() => new Set(urls?.map(u => u.id) || []), [urls])
  const accountClicks = useMemo(
    () => allClicks?.filter(c => urlIds.has(c.short_url_id)) || [],
    [allClicks, urlIds]
  )

  // Build a lookup map for slug display
  const urlMap = useMemo(() => new Map(urls?.map(u => [u.id, u]) || []), [urls])

  // Date range helper
  const getDateRangeStart = (range) => {
    if (range === 'all') return null
    const now = new Date()
    switch (range) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate())
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case 'month':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      default:
        return null
    }
  }

  // Memoized filtering pipeline
  const filteredClicks = useMemo(() => {
    let result = accountClicks

    // URL filter
    if (selectedUrlId) {
      result = result.filter(c => c.short_url_id === selectedUrlId)
    }

    // Date range filter
    const startDate = getDateRangeStart(dateRange)
    if (startDate) {
      result = result.filter(c => new Date(c.timestamp) >= startDate)
    }

    // Device filter
    if (deviceFilter !== 'all') {
      result = result.filter(c => {
        const type = c.device_type || 'desktop'
        if (deviceFilter === 'mobile') return type === 'mobile'
        if (deviceFilter === 'tablet') return type === 'tablet'
        if (deviceFilter === 'desktop') return !c.device_type || type === 'desktop'
        return true
      })
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(c =>
        (c.referrer && c.referrer.toLowerCase().includes(query)) ||
        (c.client_name && c.client_name.toLowerCase().includes(query)) ||
        (c.operating_system_name && c.operating_system_name.toLowerCase().includes(query)) ||
        (c.device_brand && c.device_brand.toLowerCase().includes(query))
      )
    }

    return result
  }, [accountClicks, selectedUrlId, dateRange, deviceFilter, searchQuery])

  // Stats computed from filtered clicks
  const stats = useMemo(() => {
    const total = filteredClicks.length
    const uniqueReferrers = new Set(filteredClicks.filter(c => c.referrer).map(c => c.referrer)).size
    const mobileTabletCount = filteredClicks.filter(c => c.device_type === 'mobile' || c.device_type === 'tablet').length
    const desktopCount = filteredClicks.filter(c => !c.device_type || c.device_type === 'desktop').length
    const mobilePercent = total > 0 ? Math.round((mobileTabletCount / total) * 100) : 0
    const desktopPercent = total > 0 ? Math.round((desktopCount / total) * 100) : 0
    return { total, uniqueReferrers, mobilePercent, desktopPercent }
  }, [filteredClicks])

  const hasActiveFilters = selectedUrlId || dateRange !== 'all' || deviceFilter !== 'all' || searchQuery.trim()

  const clearFilters = () => {
    setSelectedUrlId(null)
    setDateRange('all')
    setDeviceFilter('all')
    setSearchQuery('')
  }

  const formatTimestamp = (ts) => {
    if (!ts) return '-'
    return new Date(ts).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const truncate = (str, maxLen = 40) => {
    if (!str) return ''
    return str.length > maxLen ? str.slice(0, maxLen) + '...' : str
  }

  const getDeviceBadge = (deviceType) => {
    if (deviceType === 'mobile') {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
          mobile
        </span>
      )
    }
    if (deviceType === 'tablet') {
      return (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200">
          tablet
        </span>
      )
    }
    return (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
        desktop
      </span>
    )
  }

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
  ]

  if (!selectedAccount) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No account selected</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Please select an account from the Accounts tab first.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">Failed to load click events: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Click Analytics</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Click events for: {selectedAccount.name}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range Pills */}
          <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
            {dateRangeOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDateRange(opt.value)}
                className={`px-3 py-2 text-sm font-medium transition ${
                  dateRange === opt.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* URL Dropdown */}
          <select
            value={selectedUrlId || ''}
            onChange={e => setSelectedUrlId(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All URLs</option>
            {urls?.map(u => (
              <option key={u.id} value={u.id}>{u.slug}</option>
            ))}
          </select>

          {/* Device Dropdown */}
          <select
            value={deviceFilter}
            onChange={e => setDeviceFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Devices</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
            <option value="desktop">Desktop</option>
          </select>

          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px]">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search referrer, browser, OS, brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Active Filter Indicator */}
      {hasActiveFilters && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Showing {filteredClicks.length} of {accountClicks.length} click events
          </p>
          <button onClick={clearFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
            Clear all filters
          </button>
        </div>
      )}

      {/* Result Counter */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Showing {filteredClicks.length} of {accountClicks.length} events
      </p>

      {/* Summary stats cards with icons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Clicks */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Clicks</p>
              <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>

        {/* Unique Referrers */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <svg className="w-6 h-6 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Unique Referrers</p>
              <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{stats.uniqueReferrers}</p>
            </div>
          </div>
        </div>

        {/* Mobile / Tablet % */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Mobile / Tablet</p>
              <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{stats.mobilePercent}%</p>
            </div>
          </div>
        </div>

        {/* Desktop % */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Desktop</p>
              <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{stats.desktopPercent}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Click events table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Browser
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  OS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Referrer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredClicks.map((event) => {
                const url = urlMap.get(event.short_url_id)
                return (
                  <tr
                    key={event.id}
                    onClick={() => setSelectedClick(event)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatTimestamp(event.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {url?.slug || event.short_url_id}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getDeviceBadge(event.device_type)}
                        {event.device_brand && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {event.device_brand}{event.device_model ? ` ${event.device_model}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {event.client_name || '-'}
                      {event.client_version && (
                        <span className="text-gray-400 dark:text-gray-500 ml-1">{event.client_version}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {event.operating_system_name || '-'}
                      {event.operating_system_version && (
                        <span className="text-gray-400 dark:text-gray-500 ml-1">{event.operating_system_version}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" title={event.referrer || ''}>
                      {event.referrer ? truncate(event.referrer) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">Direct</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedClick(event)
                        }}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {filteredClicks.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No click events</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {hasActiveFilters
                ? 'No click events match the current filters.'
                : 'Click events will appear here when your short URLs are visited.'}
            </p>
          </div>
        )}
      </div>

      {/* Click Detail Modal — Push-style layout */}
      {selectedClick && (
        <Modal title="Click Event Details" onClose={() => setSelectedClick(null)} size="lg">
          <div className="space-y-6">
            {/* Basic Info — gray box style */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Short URL
                </label>
                <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded font-mono">
                  /{urlMap.get(selectedClick.short_url_id)?.slug || selectedClick.short_url_id}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Timestamp
                </label>
                <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                  {formatTimestamp(selectedClick.timestamp)}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Referrer
                </label>
                <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded break-all">
                  {selectedClick.referrer || <span className="text-gray-400 italic">Direct visit (no referrer)</span>}
                </p>
              </div>
            </div>

            {/* Device / Browser / OS Timeline — numbered circles like Push */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Click Event Details
              </label>
              <div className="space-y-3">
                {/* Device Info */}
                <div className="flex items-start space-x-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-blue-500">
                      1
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded font-semibold ${
                        selectedClick.device_type === 'mobile' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        selectedClick.device_type === 'tablet' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {selectedClick.device_type || 'desktop'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Device</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <p className="text-gray-500 dark:text-gray-400">Brand: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.device_brand || '-'}</span></p>
                      <p className="text-gray-500 dark:text-gray-400">Model: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.device_model || '-'}</span></p>
                      <p className="text-gray-500 dark:text-gray-400">Touch: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.is_touch_enabled ? 'Yes' : 'No'}</span></p>
                    </div>
                  </div>
                </div>

                {/* Browser Info */}
                <div className="flex items-start space-x-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-indigo-500">
                      2
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="px-2 py-0.5 text-xs rounded font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        {selectedClick.client_name || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Browser</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <p className="text-gray-500 dark:text-gray-400">Type: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.client_type || '-'}</span></p>
                      <p className="text-gray-500 dark:text-gray-400">Version: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.client_version || '-'}</span></p>
                    </div>
                  </div>
                </div>

                {/* OS Info */}
                <div className="flex items-start space-x-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-green-500">
                      3
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="px-2 py-0.5 text-xs rounded font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        {selectedClick.operating_system_name || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Operating System</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <p className="text-gray-500 dark:text-gray-400">Version: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.operating_system_version || '-'}</span></p>
                      <p className="text-gray-500 dark:text-gray-400">Platform: <span className="text-gray-900 dark:text-white font-medium">{selectedClick.operating_system_platform || '-'}</span></p>
                    </div>
                  </div>
                </div>

                {/* User Agent — expandable metadata */}
                {selectedClick.user_agent && (
                  <div className="flex items-start space-x-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gray-500">
                        4
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <details>
                        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 font-medium">
                          View raw User-Agent
                        </summary>
                        <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                          {selectedClick.user_agent}
                        </pre>
                      </details>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setSelectedClick(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
